import { useLinks } from '@hooks/use-links';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import './account-for-child-product-bullets.scss';
import { AppErrorCard } from '@components/error-card/app-error-card';
import { ScreenReaderElement } from '@components/screen-reader/screen-reader-element';
import { StandardButton } from '@eika/button';
import { Link } from '@eika/link';
import type { RequestException } from '@eika-infrastruktur/request';
import { useAccountForChildProcessRules } from '@react-query/hooks/query/use-account-for-child-process-rules';
import { formatNok } from '@utils/text-utils';
import Skeleton from 'react-loading-skeleton';

interface AccountForChildProductBulletsProps {
    header?: string;
}

export const AccountForChildProductBullets = ({ header }: AccountForChildProductBulletsProps) => (
    <>
        {header && <h2 className="typography-h3">{header}</h2>}
        <BulletPoints />
    </>
);

const BulletPoints = () => {
    const { data: childProcessRules, isPending, error } = useAccountForChildProcessRules();
    const { accountForChildProductBullets } = useTranslation();
    const { affectsScholarship, governorInvolvement } = useLinks();

    if (isPending) {
        return <BulletPointsSkeleton />;
    }

    if (error) {
        return <BulletPointsError error={error} />;
    }

    const { defaultHandoverAge, governorAmountThreshold, scholarshipAmountThreshold } = childProcessRules;

    return (
        <ul className="account-for-child-product-bullets__bullet-points">
            <li>{format(accountForChildProductBullets.bulletPoint.bothParentsAccess, defaultHandoverAge)}</li>
            <li>{accountForChildProductBullets.bulletPoint.bothParentsApproval}</li>
            <li>{accountForChildProductBullets.bulletPoint.notPartOfEstate}</li>
            <li>{format(accountForChildProductBullets.bulletPoint.lockedUntilHandover, defaultHandoverAge)}</li>
            <li>
                {format(
                    accountForChildProductBullets.bulletPoint.withdrawalsNotPossibleUntilHandover,
                    defaultHandoverAge,
                )}
            </li>
            <li>
                <Link href={affectsScholarship.url}>
                    {accountForChildProductBullets.bulletPoint.affectsScholarshipLink}
                </Link>{' '}
                {format(
                    accountForChildProductBullets.bulletPoint.affectsScholarshipRest,
                    formatNok(scholarshipAmountThreshold.value),
                    scholarshipAmountThreshold.year,
                )}
            </li>
            <li>
                <Link href={governorInvolvement.url}>
                    {accountForChildProductBullets.bulletPoint.governorInvolvementLink}
                </Link>{' '}
                {format(
                    accountForChildProductBullets.bulletPoint.affectsScholarshipRest,
                    formatNok(governorAmountThreshold.value),
                    governorAmountThreshold.year,
                )}
            </li>
        </ul>
    );
};

const BulletPointsSkeleton = () => <Skeleton height="3rem" count={2} style={{ gap: '40px' }} />;

const BulletPointsError = ({ error }: { error: RequestException }) => {
    const { refetch } = useAccountForChildProcessRules();
    const { accountForChildProductBullets, tryAgain } = useTranslation();
    return (
        <AppErrorCard
            description={accountForChildProductBullets.error}
            exception={error}
            data-testid="account-for-child-product-error"
        >
            <ScreenReaderElement text={accountForChildProductBullets.error} role="alert" />
            <StandardButton
                onClick={() => {
                    refetch();
                }}
            >
                {tryAgain}
            </StandardButton>
        </AppErrorCard>
    );
};
